import React from "react";
import { useParams } from "react-router-dom";
import cloudStore from "../store/CloudStore";
import { Button, Divider, Grid, IconButton, Paper, Typography } from "@material-ui/core";
import CheckboxSetting from "../components/SettingFields/CheckboxSetting";
import NumberSetting from "../components/SettingFields/NumberSetting";
import HandheldPasswordSetting from "../components/SettingFields/HandheldPasswordSetting";
import TextSetting from "../components/SettingFields/TextSetting";
import withIntl from "../utils/withIntl";
import MultiTextSetting from "../components/SettingFields/MultiTextSetting";
import OpeningHours from "../components/OpeningHours/OpeningHours";
import {
  AccessTime,
  Brush,
  DirectionsBike,
  FileCopy,
  LocalMall,
  Payment,
  PhoneIphone,
  Settings,
} from "@material-ui/icons";
import { updateTaMerchant } from "../store/TaMerchantApi";
import { days } from "../components/OpeningHours/WeekHours";
import PaymentMethodTakeaway from "../components/PaymentMethods/PaymentMethodTakeaway";
import AuthorizationWrapper from "../components/AuthorizationWrapper";
import SwitchSetting from "../components/SettingFields/SwitchSetting";
import AmountSetting from "../components/SettingFields/AmountSetting";
import PercentageSetting from "../components/SettingFields/PercentageSetting";
import TaMasterMerchantStatusSetting from "../components/SettingFields/TaMasterMerchantStatusSetting";
import ArraySetting from "../components/SettingFields/ArraySetting";
import HtmlSetting from "../components/SettingFields/HtmlSetting";
import TaMasterMerchantSetting from "../components/SettingFields/TaMasterMerchantSetting";
import DefaultOrderItemsSetting from "../components/SettingFields/DefaultOrderItemsSetting";
import { useDialog } from "../components/Dialogs/DialogProvider";
import CreateTaMerchant from "../components/CreateSalesArea/CreateTaMerchant";
import { smallScreenBoundary } from "../containers/Dashboard";
import TimestampOrderRules from "../components/TimestampSettings/TimestampOrderRules";
import StylingV2 from "../components/StylingSettings/StylingV2";
import PackagingsSettings from "../components/SettingFields/PackagingsSettings";

const getTaMerchantSettings = (taMerchant) => {
  let styling = {};
  let info = {};
  if (taMerchant.styling && taMerchant.styling !== "null") {
    styling = JSON.parse(taMerchant.styling);
  }
  if (taMerchant.info && taMerchant.info !== "null") {
    info = JSON.parse(taMerchant.info);
  }
  const settingGroups = [
    {
      key: "restaurant_settings",
      authenticationName: "takeaway.restaurant_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        {
          key: "active",
          color: "secondary",
          value: !taMerchant.active,
          type: "switch",
          transformValue: (value) => !value,
          originalValue: taMerchant.active,
        },
      ],
    },
    {
      key: "takeaway_settings",
      authenticationName: "takeaway.takeaway_settings",
      icon: <LocalMall fontSize={"large"} />,
      settings: [
        { key: "takeaway", value: taMerchant.takeaway, type: "boolean" },
        {
          key: "takeaway_active",
          value: !taMerchant.takeaway_active,
          type: "boolean",
          transformValue: (value) => !value,
          originalValue: taMerchant.takeaway_active,
        },
        { key: "minimum_pickup_minutes", value: taMerchant.minimum_pickup_minutes, type: "number" },
        { key: "min_amount", value: taMerchant.min_amount, type: "amount" },
        { key: "amount_with_extra_costs", value: taMerchant.amount_with_extra_costs, type: "amount" },
        { key: "extra_costs", value: taMerchant.extra_costs, type: "amount" },
      ],
    },
    {
      key: "delivery_settings",
      authenticationName: "takeaway.delivery_settings",
      icon: <DirectionsBike fontSize={"large"} />,
      settings: [
        { key: "delivery", value: taMerchant.delivery, type: "boolean" },
        {
          key: "delivery_active",
          value: !taMerchant.delivery_active,
          type: "boolean",
          transformValue: (value) => !value,
          originalValue: taMerchant.delivery_active,
        },
        { key: "do_ta_postal_code_check", value: taMerchant.do_ta_postal_code_check, type: "boolean" },
        { key: "delivery_minimum_pickup_minutes", value: taMerchant.delivery_minimum_pickup_minutes, type: "number" },
        {
          key: "extra_delivery_costs_vat",
          value: taMerchant.extra_delivery_costs_vat * 100,
          type: "percentage",
          transformValue: (value) => (value === "" ? 0 : value / 100),
          originalValue: taMerchant.extra_delivery_costs_vat,
        },
      ],
    },
    {
      key: "general_settings",
      authenticationName: "takeaway.general_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "name", value: taMerchant.name, type: "text" },
        { key: "demo", value: taMerchant.demo, type: "boolean" },
        { key: "is_tables", value: taMerchant.is_tables, type: "boolean" },
        { key: "allow_ordering_until_closing", value: taMerchant.allow_ordering_until_closing, type: "boolean" },
        { key: "max_weight_per_time_block", value: taMerchant.max_weight_per_time_block, type: "number" },
        { key: "order_max_number_of_days_ahead", value: taMerchant.order_max_number_of_days_ahead, type: "number" },
        {
          key: "extra_payment_costs_vat",
          value: taMerchant.extra_payment_costs_vat * 100,
          type: "percentage",
          transformValue: (value) => (value === "" ? 0 : value / 100),
          originalValue: taMerchant.extra_payment_costs_vat,
        },
        { key: "from_number", value: taMerchant.from_number, type: "number" },
        { key: "to_number", value: taMerchant.to_number, type: "number" },
        { key: "queue_minutes", value: taMerchant.queue_minutes, type: "number" },
        {
          key: "welcome_message",
          value: info["welcome_message"],
          type: "multitext",
          newDataKeys: ["info", "welcome_message"],
        },
        {
          key: "info_message",
          value: info["info_message"],
          type: "multitext",
          newDataKeys: ["info", "info_message"],
        },
        {
          key: "closed_message",
          value: info["closed_message"],
          type: "multitext",
          newDataKeys: ["info", "closed_message"],
        },
        {
          key: "tip",
          value: taMerchant.tip,
          type: "tip",
          newDataKeys: ["tip"],
        },
      ],
    },
    {
      key: "order_timestamp_settings",
      authenticationName: "takeaway.order_timestamp_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "asap", value: taMerchant.asap, type: "boolean" },
        { key: "number_of_blocks", value: taMerchant.number_of_blocks, type: "number" },
        { key: "minutes_per_block", value: taMerchant.minutes_per_block, type: "number" },
        { type: "timestamp_order_rules" },
      ],
    },
    {
      key: "email_sms_settings",
      authenticationName: "takeaway.email_sms_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "send_sms_ordered", value: taMerchant.send_sms_ordered, type: "boolean" },
        { key: "send_email_ordered", value: taMerchant.send_email_ordered, type: "boolean" },
        { key: "send_mail_from", value: taMerchant.send_mail_from, type: "text" },
        {
          key: "send_mails_to",
          value: taMerchant.send_mails_to,
          type: "array",
          transformValue: (value) => {
            return value.reduce((values, value) => {
              if (value.length > 0) {
                values.push(value);
              }
              return values;
            }, []);
          },
        },
        {
          key: "extra_mail_message",
          value: info["extra_mail_message"],
          type: "multitext",
          newDataKeys: ["info", "extra_mail_message"],
        },
      ],
    },
    {
      key: "form_settings",
      authenticationName: "takeaway.form_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        {
          key: "save_contact_information_in_session",
          value: info.ask_contact_details?.save_contact_information_in_session,
          type: "boolean",
          newDataKeys: ["info", "ask_contact_details", "save_contact_information_in_session"],
          transformValue: (value) => (value ? 1 : 0),
          defaultChecked:true
        },
        { key: "ask_marketing", value: taMerchant.ask_marketing, type: "boolean" },
        { key: "ask_remark", value: taMerchant.ask_remark, type: "boolean" },
        {
          key: "takeaway_name",
          value: info.ask_contact_details?.takeaway?.name,
          type: "boolean",
          newDataKeys: ["info", "ask_contact_details", "takeaway", "name"],
          transformValue: (value) => (value ? 1 : 0),
        },
        {
          key: "takeaway_email",
          value: info.ask_contact_details?.takeaway?.email,
          type: "boolean",
          newDataKeys: ["info", "ask_contact_details", "takeaway", "email"],
          transformValue: (value) => (value ? 1 : 0),
        },
        {
          key: "takeaway_phone",
          value: info.ask_contact_details?.takeaway?.phone,
          type: "boolean",
          newDataKeys: ["info", "ask_contact_details", "takeaway", "phone"],
          transformValue: (value) => (value ? 1 : 0),
        },
      ],
    },
    {
      key: "app_settings",
      authenticationName: "takeaway.app_settings",
      icon: <PhoneIphone fontSize={"large"} />,
      settings: [
        { key: "show_description", value: taMerchant.show_description, type: "boolean" },
        { key: "show_allergens", value: taMerchant.show_allergens, type: "boolean" },
      ],
    },
    {
      key: "ta_master_merchant_settings",
      authenticationName: "takeaway.ta_master_merchant_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "ta_master_merchant_id", value: taMerchant.ta_master_merchant_id, type: "ta_master_merchant_id" },
        { key: "status", value: info["status"], type: "ta_master_merchant_status", newDataKeys: ["info", "status"] },
        { key: "banner_text", value: info["banner_text"], type: "html", newDataKeys: ["info", "banner_text"] },
      ],
    },
    {
      key: "restaurant_info",
      authenticationName: "takeaway.restaurant_info",
      icon: <Settings fontSize={"large"} />,
      settings: [
        {
          key: "contact.email",
          value: info.contact_details_merchant?.email,
          type: "text",
          newDataKeys: ["info", "contact_details_merchant", "email"],
        },
        {
          key: "contact.phone_number",
          value: info.contact_details_merchant?.phone_number,
          type: "text",
          newDataKeys: ["info", "contact_details_merchant", "phone_number"],
        },
        {
          key: "contact.address",
          value: info.contact_details_merchant?.address,
          type: "text",
          newDataKeys: ["info", "contact_details_merchant", "address"],
        },
        {
          key: "contact.city",
          value: info.contact_details_merchant?.city,
          type: "text",
          newDataKeys: ["info", "contact_details_merchant", "city"],
        },
        {
          key: "contact.postal_code",
          value: info.contact_details_merchant?.postal_code,
          type: "text",
          newDataKeys: ["info", "contact_details_merchant", "postal_code"],
        },
      ],
    },
    {
      key: "iframe_settings",
      authenticationName: "takeaway.iframe_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        { key: "iframe", value: styling["iframe"], type: "boolean", newDataKeys: ["styling", "iframe"] },
        {
          key: "paynl_return_domain",
          value: info["paynl_return_domain"],
          type: "text",
          newDataKeys: ["info", "paynl_return_domain"],
        },
      ],
    },
    {
      key: "other_settings",
      authenticationName: "takeaway.others_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        {
          key: "default_order_items",
          value: info.default_order_items,
          type: "default_order_items",
          newDataKeys: ["info", "default_order_items"],
        },
        {
          key: "packagings",
          value: info.packagings,
          type: "packagings",
          newDataKeys: ["info", "packagings"],
        },
      ],
    },
  ];

  return settingGroups;
};

const getNewData = (setting, value) => {
  if (setting.transformValue) {
    value = setting.transformValue(value);
  }
  if (!setting.newDataKeys) {
    return { [setting.key]: value };
  } else {
    let object = {};
    let keys = setting.newDataKeys.reverse();
    keys.forEach((key, index) => {
      if (index === 0) {
        object[key] = value;
      } else {
        object = { [key]: object };
      }
    });
    return object;
  }
};

function TaDeSettings(props) {
  const [taMerchant, setTaMerchant] = React.useState(null);
  const taMerchantId = Number.parseInt(useParams().taMerchantId);
  const [settingGroups, setSettingGroups] = React.useState([]);
  const [openDialog, closeDialog] = useDialog();

  if (taMerchantId !== taMerchant?.id) {
    let taMerchant = cloudStore.merchant.ta_merchants.find((taMerchant) => taMerchant.id === taMerchantId);
    setTaMerchant(taMerchant);
    setSettingGroups(getTaMerchantSettings(taMerchant));
    return null;
  }
  let deliveryTimes = {};
  days.forEach((day) => {
    deliveryTimes[day] = "{}";
  });
  if (taMerchant.info) {
    let deliveryTimesTemp = JSON.parse(taMerchant.info).delivery_times;
    if (deliveryTimesTemp) {
      Object.keys(deliveryTimesTemp).forEach((day) => {
        deliveryTimes[day] = JSON.stringify(deliveryTimesTemp[day]);
      });
    }
  }

  const getTakeawayStatus = (taMerchant) => {
    if (!taMerchant.takeaway) {
      return "takeaway_deactivated";
    }
    if (!taMerchant.active) {
      return "restaurant_closed";
    }
    if (!taMerchant.takeaway_active) {
      return "takeaway_temporarily_deactivated";
    }
    return "restaurant_open";
  };

  const getDeliveryStatus = (taMerchant) => {
    if (!taMerchant.delivery) {
      return "delivery_deactivated";
    }
    if (!taMerchant.active) {
      return "restaurant_closed";
    }
    if (!taMerchant.delivery_active) {
      return "delivery_temporarily_deactivated";
    }
    return "restaurant_open";
  };

  let paymentMethods = [];
  if (taMerchant.payment_methods) {
    paymentMethods = JSON.parse(taMerchant.payment_methods);
    if (!Array.isArray(paymentMethods)) {
      paymentMethods = [];
    }
  }

  const updateTaMerchantApi = (setting, value) => {
    let newValue = setting.transformValue ? setting.transformValue(value) : value;
    if (Array.isArray(value)) {
      if (newValue.equals(setting.value)) {
        return;
      }
    } else {
      if (setting.originalValue != null) {
        if (setting.originalValue === newValue) {
          return;
        }
      } else {
        if (setting.value === newValue) {
          return;
        }
      }
    }
    updateTaMerchant(taMerchant, getNewData(setting, value)).then((taMerchant) => {
      setSettingGroups(getTaMerchantSettings(taMerchant));
      setTaMerchant(taMerchant);
    });
  };

  return (
    <div style={{ display: "flex", flexFlow: "wrap" }}>
      {window.innerWidth >= smallScreenBoundary && (
        <AuthorizationWrapper
          permission={"create_ta_merchant"}
          child={
            <IconButton
              style={{ position: "absolute", left: 300, top: 64 }}
              onClick={() => {
                openDialog({ children: <CreateTaMerchant taMerchant={taMerchant} /> });
              }}
            >
              <FileCopy />
            </IconButton>
          }
        />
      )}
      <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <AccessTime fontSize={"large"} />
          <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
            {props.intl.formatMessage({ id: "opening_hours_takeaway" })}
          </Typography>
        </div>
        <OpeningHours
          openingHours={taMerchant}
          onSave={(newData) => {
            return new Promise((resolve) => {
              updateTaMerchant(taMerchant, newData).then(() => {
                resolve();
              });
            });
          }}
        />
      </Paper>
      <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <AccessTime fontSize={"large"} />
          <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
            {props.intl.formatMessage({ id: "opening_hours_delivery" })}
          </Typography>
        </div>
        <OpeningHours
          openingHours={deliveryTimes}
          onSave={(newData) => {
            Object.keys(newData).forEach((day) => {
              newData[day] = JSON.parse(newData[day]);
            });
            return new Promise((resolve) => {
              updateTaMerchant(taMerchant, { info: { delivery_times: newData } }).then(() => {
                resolve();
              });
            });
          }}
        />
      </Paper>
      <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
        <Grid container>
          <Grid item xs={5}>
            <Typography>Takeaway status</Typography>
          </Grid>
          <Grid item xs={2}>
            {getTakeawayStatus(taMerchant) === "restaurant_open" ? (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#0F0",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                }}
              />
            ) : (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#F00",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                }}
              />
            )}
          </Grid>
          <Grid item xs={5}>
            <Typography>{props.intl.formatMessage({ id: getTakeawayStatus(taMerchant) })}</Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={5}>
            <Typography>Delivery status</Typography>
          </Grid>
          <Grid item xs={2}>
            {getDeliveryStatus(taMerchant) === "restaurant_open" ? (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#0F0",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                }}
              />
            ) : (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#F00",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                }}
              />
            )}
          </Grid>
          <Grid item xs={5}>
            <Typography>{props.intl.formatMessage({ id: getDeliveryStatus(taMerchant) })}</Typography>
          </Grid>
        </Grid>
      </Paper>
      {settingGroups.map((settingGroup, index) => {
        return (
          <AuthorizationWrapper
            permission={settingGroup.authenticationName}
            child={(write) => (
              <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {settingGroup.icon && settingGroup.icon}
                  <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                    {props.intl.formatMessage({ id: settingGroup.key })}
                  </Typography>
                </div>
                {settingGroup.settings.map((setting, index) => {
                  switch (setting.type) {
                    case "boolean":
                      return (
                        <CheckboxSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                          defaultChecked={setting.defaultChecked}
                        >
                          <Divider />
                        </CheckboxSetting>
                      );
                    case "switch":
                      return (
                        <SwitchSetting
                          color={setting.color}
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider />
                        </SwitchSetting>
                      );
                    case "number":
                      return (
                        <NumberSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </NumberSetting>
                      );
                    case "amount":
                      return (
                        <AmountSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </AmountSetting>
                      );
                    case "percentage":
                      return (
                        <PercentageSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </PercentageSetting>
                      );

                    case "number_password":
                      return (
                        <HandheldPasswordSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </HandheldPasswordSetting>
                      );
                    case "text":
                      return (
                        <TextSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </TextSetting>
                      );
                    case "ta_master_merchant_status":
                      return (
                        <TaMasterMerchantStatusSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </TaMasterMerchantStatusSetting>
                      );
                    case "ta_master_merchant_id":
                      return (
                        <TaMasterMerchantSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </TaMasterMerchantSetting>
                      );
                    case "array":
                      return (
                        <ArraySetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </ArraySetting>
                      );
                    case "multitext":
                      return (
                        <MultiTextSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </MultiTextSetting>
                      );
                    case "html":
                      return (
                        <HtmlSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </HtmlSetting>
                      );
                    case "default_order_items":
                      return (
                        <DefaultOrderItemsSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </DefaultOrderItemsSetting>
                      );
                    case "packagings":
                      return (
                        <PackagingsSettings
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateTaMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </PackagingsSettings>
                      );
                    case "timestamp_order_rules":
                      return (
                        <AuthorizationWrapper
                          permission={"ta_merchant.order_hours"}
                          child={
                            <Button
                              variant={"outlined"}
                              onClick={() => {
                                openDialog({
                                  children: (
                                    <TimestampOrderRules
                                      fullWidth={true}
                                      salesArea={taMerchant}
                                      onSave={(orderRules) => {
                                        updateTaMerchant(taMerchant, { order_rules: orderRules }).then((taMerchant) => {
                                          setTaMerchant(taMerchant);
                                          closeDialog();
                                        });
                                      }}
                                    />
                                  ),
                                });
                              }}
                            >
                              {props.intl.formatMessage({ id: "order_hours" })}
                            </Button>
                          }
                        />
                      );
                    case "tip":
                      return (
                        <SwitchSetting
                          color={setting.color}
                          label={props.intl.formatMessage({ id: "Tip" })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + taMerchant.updated_at}
                          setting={{
                            ...setting,
                            ...{ value: setting.value?.options && Object.keys(setting.value.options).length > 0 },
                          }}
                          onChange={(value) => {
                            let tipData = value
                              ? { options: [{ free: 1 }, { percentage: 5 }, { percentage: 10 }, { percentage: 15 }] }
                              : null;
                            updateTaMerchantApi(setting, tipData);
                          }}
                        >
                          <Divider />
                        </SwitchSetting>
                      );
                    default:
                      return (
                        <>
                          <Typography variant={"h6"}>
                            Coming soon {props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          </Typography>
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </>
                      );
                  }
                })}
              </Paper>
            )}
          />
        );
      })}
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "styling" })}
              </Typography>
            </div>
            <StylingV2
              main={true}
              styling={JSON.parse(taMerchant.styling)}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: styling }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Header image styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontType={false}
              styling={JSON.parse(taMerchant.styling)?.header_image}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { header_image: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Articlegroup styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(taMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(taMerchant.styling)?.articlegroup}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { articlegroup: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColorDefault={JSON.parse(taMerchant.styling)?.palette?.text?.primary}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(taMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(taMerchant.styling)?.article}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { article: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article description styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(taMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(taMerchant.styling)?.article_description}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { article_description: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article price styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(taMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(taMerchant.styling)?.article_price}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { article_price: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Top bar styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColorDefault={JSON.parse(taMerchant.styling)?.palette?.primary?.main}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColorDefault={JSON.parse(taMerchant.styling)?.palette?.text?.primary}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(taMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(taMerchant.styling)?.top_navigation}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { styling: { top_navigation: styling } }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"takeaway.payment_methods"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Payment fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "payment_methods" })}
              </Typography>
            </div>
            <PaymentMethodTakeaway
              paymentMethods={paymentMethods}
              onSave={(paymentMethods) => {
                return new Promise((resolve, reject) => {
                  updateTaMerchant(taMerchant, { payment_methods: paymentMethods }).then((taMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
    </div>
  );
}

export default withIntl(TaDeSettings);
